import { REDIRECT_URL_KEY } from "../constants/app.constant";
import { getAuthenticationReturnToFromSearch } from "../router/auth-return-intent";

const KEY = "indicafix.invitationReturn";
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,512}$/;

export const captureInvitationToken = (url: URL): string | null => {
  const token = url.searchParams.get("token");
  if (token === null) return null;
  const captured = retainInvitationToken(token);
  url.searchParams.delete("token");
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
  return captured;
};

const retainInvitationToken = (token: string) => {
  clearInvitationAcceptance();
  const captured = TOKEN_PATTERN.test(token) ? token : null;
  // Persist before notifying router history subscribers so a remount cannot
  // observe an older invitation when a newer link replaces it.
  if (captured) sessionStorage.setItem(KEY, captured);
  else sessionStorage.removeItem(KEY);
  return captured;
};

export const captureInvitationEntry = (url: URL): boolean => {
  if (url.pathname === "/company-invitation") {
    captureInvitationToken(url);
    return true;
  }
  const returnTo = new URL(
    getAuthenticationReturnToFromSearch(url.search),
    url.origin,
  );
  if (returnTo.pathname !== "/company-invitation") return false;
  const token = returnTo.searchParams.get("token");
  if (token !== null) {
    retainInvitationToken(token);
    returnTo.searchParams.delete("token");
    url.searchParams.set(
      REDIRECT_URL_KEY,
      `${returnTo.pathname}${returnTo.search}${returnTo.hash}`,
    );
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  }
  return true;
};

export const readInvitationToken = () => sessionStorage.getItem(KEY);
export const clearInvitationToken = () => {
  sessionStorage.removeItem(KEY);
  clearInvitationAcceptance();
};

// A receipt records only the user's explicit attempt, never grants membership.
// The current authenticated account must be reconciled with the server before
// using it to continue after a lost response or a reload.
const RECEIPT_KEY = "indicafix.invitationAcceptance";
export type InvitationAcceptanceReceipt = {
  accountId: string;
  companyId: string;
  companyRole: "Owner" | "Editor" | "Viewer";
};
export const readInvitationAcceptance =
  (): InvitationAcceptanceReceipt | null => {
    try {
      const value = JSON.parse(sessionStorage.getItem(RECEIPT_KEY) ?? "null");
      return value &&
        typeof value.accountId === "string" &&
        typeof value.companyId === "string" &&
        ["Owner", "Editor", "Viewer"].includes(value.companyRole)
        ? value
        : null;
    } catch {
      return null;
    }
  };
export const recordInvitationAcceptance = (
  receipt: InvitationAcceptanceReceipt,
) => {
  sessionStorage.setItem(RECEIPT_KEY, JSON.stringify(receipt));
};
export const clearInvitationAcceptance = () =>
  sessionStorage.removeItem(RECEIPT_KEY);
